/**
 * React Query Client Configuration
 * Setup for Tanstack Query with sensible defaults
 */

import { QueryClient } from '@tanstack/query-core';
import { captureException, addBreadcrumb } from './sentry/sentry-utils';
import { ERROR_LEVELS, TAGS } from '../constants/sentry-constants';

/**
 * Preserve error structure for Sentry capture
 * Maintains original error type and context without losing information
 */
function preserveErrorForSentry(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  
  // For non-Error types, create an Error but preserve the original in extra context
  const errorMessage = String(error);
  const preservedError = new Error(errorMessage);
  
  // Attach original error as a property for context
  (preservedError as Error & { originalError: unknown }).originalError = error;
  
  return preservedError;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is immediately stale so background refetch triggers on every mount
      staleTime: 0,
      // Keep unused queries in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed queries up to 3 times
      retry: 3,
      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus in development
      refetchOnWindowFocus: import.meta.env.PROD,
      // Refetch on reconnect
      refetchOnReconnect: true,
      // Refetch stale data in the background on mount (show old data immediately, update when fresh arrives)
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations once on network errors
      retry: 1,
      // Exponential backoff for mutations
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  },
});

// Set up global error handlers using queryCache and mutationCache subscriptions
// This is the v5 way to handle errors globally (onError was removed in v5)
// We check for errors when queries/mutations are updated

// Query error handler
queryClient.getQueryCache().subscribe((event) => {
  if (event?.type === 'updated' && event.query) {
    const error = event.query.state.error;
    
    // Only capture error if query is in error state
    if (error && event.query.state.status === 'error') {
      // Add breadcrumb for query error
      addBreadcrumb({
        category: 'query',
        message: `Query error: ${event.query.queryKey.join('/')}`,
        level: ERROR_LEVELS.ERROR,
        data: {
          queryKey: event.query.queryKey,
          error: error instanceof Error ? error.message : String(error),
        },
      });

      // Capture error to Sentry - preserve original error type
      const preservedError = preserveErrorForSentry(error);
      captureException(preservedError, {
        tags: {
          [TAGS.ERROR_TYPE]: 'query',
          queryKey: event.query.queryKey.join('/'),
        },
        extra: {
          originalErrorType: error instanceof Error ? error.constructor.name : typeof error,
          originalError: error instanceof Error ? undefined : error,
        },
        contexts: {
          reactQuery: {
            queryKey: event.query.queryKey,
            queryHash: event.query.queryHash,
            state: event.query.state,
          },
        },
      });
    }
  }
});

// Mutation error handler
queryClient.getMutationCache().subscribe((event) => {
  if (event?.type === 'updated' && event.mutation) {
    const error = event.mutation.state.error;
    
    // Only capture error if mutation is in error state
    if (error && event.mutation.state.status === 'error') {
      // Get mutation key from options
      const mutationKey = event.mutation.options.mutationKey;
      const mutationKeyString = mutationKey ? (Array.isArray(mutationKey) ? mutationKey.join('/') : String(mutationKey)) : 'unknown';
      
      // Add breadcrumb for mutation error
      addBreadcrumb({
        category: 'mutation',
        message: `Mutation error: ${mutationKeyString}`,
        level: ERROR_LEVELS.ERROR,
        data: {
          mutationKey,
          error: error instanceof Error ? error.message : String(error),
        },
      });

      // Capture error to Sentry - preserve original error type
      const preservedError = preserveErrorForSentry(error);
      captureException(preservedError, {
        tags: {
          [TAGS.ERROR_TYPE]: 'mutation',
          mutationKey: mutationKeyString,
        },
        extra: {
          originalErrorType: error instanceof Error ? error.constructor.name : typeof error,
          originalError: error instanceof Error ? undefined : error,
        },
        contexts: {
          reactQuery: {
            mutationKey,
            variables: event.mutation.state.variables,
          },
        },
      });
    }
  }
});

/**
 * Invalidate queries by key patterns
 */
export const invalidateQueries = (queryKey: string[]) => {
  return queryClient.invalidateQueries({ queryKey });
};

/** Refetches now, lists off screen too, so one opened later never shows rows from before the change. */
export const refreshQueries = (queryKey: string[]) => {
  return queryClient.invalidateQueries({ queryKey, refetchType: 'all' });
};

/**
 * Clear all query cache
 *
 * Tears the cache down completely. Correct when the identity behind every row changes and
 * the page is about to reload anyway - sign in, sign out, account switch. NOT correct while
 * the user stays on the page: it removes the query objects mounted lists are subscribed to
 * and kills their in-flight fetches, leaving every visible table on a "Loading..." that
 * never resolves. A brand change stays on the page and needs no invalidation at all - the
 * brand is part of every scoped query key.
 */
export const clearQueryCache = () => {
  queryClient.clear();
};

